import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAdminFeedback } from "@/lib/api";
import { FEEDBACK_CATEGORIES } from "@/lib/feedback";

import { FeedbackBarChart } from "./feedback-bar-chart";
import { FeedbackEntryCard } from "./feedback-entry";

const PAGE_SIZE = 10;

const RATING_OPTIONS = [
  { value: "0", label: "No rating" },
  { value: "1", label: "1 star" },
  { value: "2", label: "2 stars" },
  { value: "3", label: "3 stars" },
  { value: "4", label: "4 stars" },
  { value: "5", label: "5 stars" },
];

/**
 * Interactive feedback browser. Search, filters, and pagination are all
 * server-side — every control change re-runs `useAdminFeedback` with new
 * params. The distribution chart reads the full-table buckets that ride
 * along on the page payload, so it stays stable while the list narrows.
 */
export function FeedbackExplorer() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState("");
  const [page, setPage] = useState(1);

  // Debounce the search box so we don't query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const feedbackQuery = useAdminFeedback({
    search: debouncedSearch || undefined,
    category: category || undefined,
    rating: rating === "" ? undefined : Number(rating),
    page,
    pageSize: PAGE_SIZE,
  });

  if (feedbackQuery.isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="text-muted h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (feedbackQuery.isError || !feedbackQuery.data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted text-sm">
            Could not load feedback. Please{" "}
            <button
              type="button"
              onClick={() => void feedbackQuery.refetch()}
              className="underline"
            >
              try again
            </button>{" "}
            or{" "}
            <Link to="/home" className="underline">
              go back
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    );
  }

  const data = feedbackQuery.data;
  const loading = feedbackQuery.isFetching;
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const from = data.total === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(data.page * PAGE_SIZE, data.total);
  const hasEntries = data.distribution.some((bucket) => bucket.count > 0);

  return (
    <div className="space-y-6">
      {hasEntries && <FeedbackBarChart distribution={data.distribution} />}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search message, user, type, or path..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="border-border bg-background h-9 rounded-md border px-3 text-sm"
        >
          <option value="">All types</option>
          {FEEDBACK_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={rating}
          onChange={(e) => {
            setRating(e.target.value);
            setPage(1);
          }}
          className="border-border bg-background h-9 rounded-md border px-3 text-sm"
        >
          <option value="">All ratings</option>
          {RATING_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {data.items.length === 0 ? (
        <p className="text-muted py-8 text-center text-sm">
          {data.total === 0 && !debouncedSearch && !category && !rating
            ? "No feedback yet."
            : "No feedback matches those filters."}
        </p>
      ) : (
        <ul className="relative space-y-3">
          {loading && (
            <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
              <span className="text-muted text-xs">Loading…</span>
            </div>
          )}
          {data.items.map((entry) => (
            <FeedbackEntryCard key={entry.id} entry={entry} />
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted">
          {from}–{to} of {data.total}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={data.page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          <span className="text-muted">
            Page {data.page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={data.page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
